import {gql} from '@apollo/client';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import type {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import type {
  ReadOnlyFlowchartRootTaskSchemaQuery,
  ReadOnlyFlowchartRootTaskSchemaQueryVariables,
} from './__generated/ReadOnlyFlowchartRootTaskSchemaQuery';

export enum ReadOnlyFlowchartRootTaskSchemaResultType {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  INACCESSIBLE = 'INACCESSIBLE',
  ERROR = 'ERROR',
}

export type ReadOnlyFlowchartRootTaskSchemaResult =
  | {
      type: ReadOnlyFlowchartRootTaskSchemaResultType.PENDING;
      schema: null;
    }
  | {
      type: ReadOnlyFlowchartRootTaskSchemaResultType.SUCCESS;
      schema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
    }
  | {
      type: ReadOnlyFlowchartRootTaskSchemaResultType.INACCESSIBLE;
      schema: null;
    }
  | {
      type: ReadOnlyFlowchartRootTaskSchemaResultType.ERROR;
      schema: null;
    };

export const READ_ONLY_FLOWCHART_ROOT_TASK_SCHEMA_QUERY = gql`
  query ReadOnlyFlowchartRootTaskSchemaQuery($taskSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $taskSchemaId) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaWithoutFlowchartConfigGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTaskSchemaWithoutFlowchartConfig').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function useReadOnlyFlowchartRootTaskSchema(
  taskSchemaId: string
): ReadOnlyFlowchartRootTaskSchemaResult {
  const result = useSkippableBenchlingQuery<
    ReadOnlyFlowchartRootTaskSchemaQuery,
    ReadOnlyFlowchartRootTaskSchemaQueryVariables
  >(READ_ONLY_FLOWCHART_ROOT_TASK_SCHEMA_QUERY, {
    variables: {taskSchemaId},
    skip: !taskSchemaId,
  });

  if (result.type === QueryResultType.PENDING) {
    return {type: ReadOnlyFlowchartRootTaskSchemaResultType.PENDING, schema: null};
  }
  if (result.type === QueryResultType.ERROR || result.type === QueryResultType.SKIPPED) {
    return {type: ReadOnlyFlowchartRootTaskSchemaResultType.ERROR, schema: null};
  }
  if (isInaccessible(result.data.processTaskSchema)) {
    return {type: ReadOnlyFlowchartRootTaskSchemaResultType.INACCESSIBLE, schema: null};
  }
  return {
    type: ReadOnlyFlowchartRootTaskSchemaResultType.SUCCESS,
    schema: result.data.processTaskSchema,
  };
}
